import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon, Popover } from "antd";
import ReactGA from 'react-ga4';

import styles from "./priceComparison.scss";
import { PageRoute, QueryParam } from "../../../models";
import { summaryInterface } from "../../../typings/interfaces";
import { useLocation } from "../../../routes/Configuration/hooks";
import { parsePriceString, stringifyURI } from "../../../services/utils";
import { getIconUrl } from "../../../services/asset";

// Fixed track positions for the MIN/PROSJEK/MAX dots — pinned at the track
// extremes so MIN/MAX always read as the edges.
const MIN_DOT_POS = 0;
const AVG_DOT_POS = 50;
const MAX_DOT_POS = 100;
// When the marker is within this distance of MIN/MAX, anchor its price pill to
// the MIN/MAX dot (left- or right-aligned) instead of centering on the marker
// so the pill stays on the card and visually attaches to MIN/MAX.
const MARKER_EDGE_THRESHOLD = 10;

interface PriceComparisonProps {
  adSummary: summaryInterface;
  category?: string;
  module?: string;
}

interface SettlementInfo {
  name: string;
  route: string;
  id: string;
}

// The settlement name comes with the ad summary; its url slug is fetched
// through configuration/locations/:id (see useLocation below).
const findSettlementInfo = (settlementRoute: string | null | undefined, adSummary: summaryInterface): SettlementInfo | null => {
  if (!adSummary || !adSummary.settlementId || !adSummary.settlementName || !settlementRoute) return null;

  return {
    name: adSummary.settlementName,
    route: settlementRoute,
    id: adSummary.settlementId
  };
};

const buildSettlementSearchUrl = (module: string, category: string, settlementRoute: string, settlementId: string): string => {
  const queryParams = {
    category: category,
    module: module,
    includeSettlementIds: [settlementId]
  };
  const queryString = stringifyURI(queryParams, QueryParam.searchQuery, false);
  return "/" + module + "/" + category + "/" + settlementRoute + PageRoute.search + queryString;
};

const formatPrice = (value: number): string => parsePriceString(Math.round(value));
const formatPriceM2 = (value: number): string => `${parsePriceString(Math.round(value))}/m²`;

const explanationContent = (
  <div className={styles.explanationContent}>
    <p>
      {"Cijenu po m² ovog oglasa uspoređujemo s prosjekom sličnih nekretnina, iste vrste i na istoj lokaciji, na temelju oglasa iz prethodnog mjeseca. Marker pokazuje nalazi li se ovaj oglas bliže najnižoj, prosječnoj ili najvišoj cijeni po m² na tom području."}
    </p>
    <p>
      {"Cijena po m² računa se iz cijene i stambene površine nekretnine i ne uzima u obzir opremljenost, stanje, okućnicu ni dodatke poput bazena ili garaže. Zato je ovo orijentacija, a ne procjena vrijednosti nekretnine."}
    </p>
    <p>
      {"Prosjek prikazujemo samo kada za to područje postoji dovoljno oglasa za pouzdanu usporedbu."}
    </p>
  </div>
);

const PriceComparison = ({ adSummary, category, module }: PriceComparisonProps) => {
  const settlement = useLocation(adSummary && adSummary.settlementId);

  const [cardNode, setCardNode] = useState(null);
  useEffect(() => {
    if (!cardNode) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          ReactGA.event({
            category: "Widget cijene m2 u oglasu",
            action: "Click",
            label: `Prikazan u oglasu`
          });
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );
    observer.observe(cardNode);
    return () => observer.disconnect();
  }, [cardNode])

  const onVisibleChange = (visible) => {
    if (visible) {
      ReactGA.event({
        category: "Widget cijene m2 u oglasu",
        action: "Click",
        label: `Klik na tooltip`
      });
    }
  }

  const handleSettlementClick = (label) => {
    ReactGA.event({
      category: "Widget cijene m2 u oglasu",
      action: "Click",
      label: `Klik na naselje ${label}`
    });
  }

  const { priceM2, averagePriceM2, lowerRangePriceM2, higherRangePriceM2 } = adSummary || {};
  if (!priceM2 || !averagePriceM2 || !lowerRangePriceM2 || !higherRangePriceM2) return null;
  if (higherRangePriceM2 <= lowerRangePriceM2) return null;

  // 3-stop value scale: MIN at 0, PROSJEK at 50, MAX at 100. Interpolate current
  // price within the appropriate half, allowing values past 100 / below 0 when the
  // ad's priceM2 is outside the comparable range.
  let valuePos: number;
  if (priceM2 <= averagePriceM2) {
    const span = averagePriceM2 - lowerRangePriceM2;
    valuePos = span > 0 ? ((priceM2 - lowerRangePriceM2) / span) * 50 : 0;
  } else {
    const span = higherRangePriceM2 - averagePriceM2;
    valuePos = span > 0 ? 50 + ((priceM2 - averagePriceM2) / span) * 50 : 100;
  }

  // Marker tracks valuePos 1:1 when the price is comfortably inside [MIN, MAX].
  // Within MARKER_EDGE_THRESHOLD of either edge the marker snaps onto the MIN
  // or MAX dot so the price pill sits flush with the dot (no half-track gap).
  const minPos = MIN_DOT_POS;
  const avgPos = AVG_DOT_POS;
  const maxPos = MAX_DOT_POS;
  let markerPos: number;
  let markerAlignClass = styles.markerCenter;
  if (valuePos <= MIN_DOT_POS + MARKER_EDGE_THRESHOLD) {
    markerPos = MIN_DOT_POS;
    markerAlignClass = styles.markerLeft;
  } else if (valuePos >= MAX_DOT_POS - MARKER_EDGE_THRESHOLD) {
    markerPos = MAX_DOT_POS;
    markerAlignClass = styles.markerRight;
  } else {
    markerPos = valuePos;
  }

  const diffPct = Math.round(((priceM2 - averagePriceM2) / averagePriceM2) * 100);
  const settlementInfo = findSettlementInfo(settlement && settlement.route, adSummary);
  const settlementName = settlementInfo ? settlementInfo.name : "";
  const settlementSearchUrl = settlementInfo && category && module
    ? buildSettlementSearchUrl(module, category, settlementInfo.route, settlementInfo.id)
    : null;

  const renderSettlementLink = (label) => {
    if (!settlementName) return null;
    if (settlementSearchUrl) {
      return (
        <Link className={styles.settlementLink} to={settlementSearchUrl} onClick={() => handleSettlementClick(label)}>
          {settlementName}
        </Link>
      );
    }
    return <span className={styles.settlement}>{settlementName}</span>;
  };

  const renderLocationSuffix = () =>
    settlementName ? (
      <React.Fragment>
        {" za naselje "}
        {renderSettlementLink("dole")}
        {"."}
      </React.Fragment>
    ) : (
      " u lokaciji."
    );

  const renderFooter = () => {
    if (diffPct === 0) {
      return (
        <React.Fragment>
          {"Ovaj oglas je "}
          <span className={styles.footerEmphasis}>{"u rangu prosjeka"}</span>
          {renderLocationSuffix()}
        </React.Fragment>
      );
    }
    const direction = diffPct > 0 ? "iznad" : "ispod";
    return (
      <React.Fragment>
        {"Ovaj oglas je "}
        <span className={styles.footerEmphasis}>{`${Math.abs(diffPct)}% ${direction}`}</span>
        {" prosjeka"}
        {renderLocationSuffix()}
      </React.Fragment>
    );
  };

  return (
    <div ref={setCardNode} className={styles.card}>
      <div className={styles.header}>
        <img className={styles.icon} src={getIconUrl("bar-chart.svg")} alt="" />
        <span className={styles.title}>
          {"Cijena po m² u odnosu na druge oglase"}
          {settlementName && (
            <React.Fragment>
              {" za naselje "}
              {renderSettlementLink("gore")}
            </React.Fragment>
          )}
          <Popover
            title={"Kako čitati ovu usporedbu"}
            content={explanationContent}
            placement="bottom"
            overlayClassName={styles.explanationPopover}
            onVisibleChange={onVisibleChange}
          >
            <Icon type="info-circle-o" className={styles.infoIcon} />
          </Popover>
        </span>
      </div>

      <div className={styles.scale}>
        <div className={styles.labels}>
          <div className={`${styles.label} ${styles.labelLeft}`} style={{ left: `${minPos}%` }}>
            <span className={styles.labelTag}>{"MIN"}</span>
            <span className={styles.labelValue}>{formatPrice(lowerRangePriceM2)}</span>
          </div>
          <div className={`${styles.label} ${styles.labelCenter}`} style={{ left: `${avgPos}%` }}>
            <span className={styles.labelTag}>{"PROSJEK"}</span>
            <span className={styles.labelValue}>{formatPrice(averagePriceM2)}</span>
          </div>
          <div className={`${styles.label} ${styles.labelRight}`} style={{ left: `${maxPos}%` }}>
            <span className={styles.labelTag}>{"MAX"}</span>
            <span className={styles.labelValue}>{formatPrice(higherRangePriceM2)}</span>
          </div>
        </div>

        <div className={styles.track}>
          <div className={styles.trackLine} />
          <div className={styles.trackDot} style={{ left: `${minPos}%` }} />
          <div className={styles.trackDot} style={{ left: `${avgPos}%` }} />
          <div className={styles.trackDot} style={{ left: `${maxPos}%` }} />
          <img
            className={styles.priceIndicator}
            src={getIconUrl("price-indicator.svg")}
            style={{ left: `${markerPos}%` }}
            alt=""
          />
          <div className={`${styles.marker} ${markerAlignClass}`} style={{ left: `${markerPos}%` }}>
            <span className={styles.markerBubble}>{formatPriceM2(priceM2)}</span>
          </div>
        </div>
      </div>

      <div className={styles.footer}>{renderFooter()}</div>
    </div>
  );
};

export default PriceComparison;



// WEBPACK FOOTER //
// ./src/containers/AdDetailsPage/PriceComparison/index.tsx